import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/services/auth.service';

export type TwoFaMethod = 'totp' | 'email';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  totpRequired: boolean;
  twoFaMethod: TwoFaMethod | null;
  tempToken: string | null;
  setUser: (user: User | null) => void;
  setTotpRequired: (required: boolean) => void;
  setTwoFaMethod: (method: TwoFaMethod | null) => void;
  setTempToken: (token: string | null) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      totpRequired: false,
      twoFaMethod: null,
      tempToken: null,
      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          totpRequired: false,
          twoFaMethod: null,
          tempToken: null,
        }),
      setTotpRequired: (totpRequired) => set({ totpRequired }),
      setTwoFaMethod: (twoFaMethod) => set({ twoFaMethod }),
      setTempToken: (tempToken) => set({ tempToken }),
      clearAuth: () =>
        set({
          user: null,
          isAuthenticated: false,
          totpRequired: false,
          twoFaMethod: null,
          tempToken: null,
        }),
    }),
    {
      name: 'auth-storage',
      // Only persist non-sensitive data
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// UI State
interface UIState {
  sidebarExpanded: boolean;
  language: 'en' | 'de';
  toggleSidebar: () => void;
  setSidebarExpanded: (expanded: boolean) => void;
  setLanguage: (lang: 'en' | 'de') => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarExpanded: false,
      language: 'en',
      toggleSidebar: () => set((state) => ({ sidebarExpanded: !state.sidebarExpanded })),
      setSidebarExpanded: (expanded) => set({ sidebarExpanded: expanded }),
      setLanguage: (language) => set({ language }),
    }),
    {
      name: 'ui-storage',
    }
  )
);

// Registration Flow State
interface Step2Data {
  email: string;
  password: string;
  confirmPassword?: string;
  companyName: string;
  companyWebsite?: string;
  phoneNumber: string;
  country?: string;
}

interface RegistrationState {
  step: number;
  // Granular platform the user picked (e.g. 'braze').
  emailPlatform?: string;
  // Collapsed flow-branching value ('klaviyo' | 'other').
  emailMarketingTool?: string;
  email?: string;
  companyName?: string;
  step2Data?: Step2Data;
  setStep: (step: number) => void;
  setPlatformSelection: (platform: string, tool: string) => void;
  setCompanyDetails: (email: string, companyName: string) => void;
  setStep2Data: (data: Step2Data) => void;
  resetRegistration: () => void;
}

export const useRegistrationStore = create<RegistrationState>()(
  persist(
    (set) => ({
      step: 1,
      setStep: (step) => set({ step }),
      setPlatformSelection: (platform, tool) =>
        set({ emailPlatform: platform, emailMarketingTool: tool, step: 2 }),
      setCompanyDetails: (email, companyName) => set({ email, companyName, step: 3 }),
      setStep2Data: (data) => set({ step2Data: data }),
      resetRegistration: () => set({
        step: 1,
        emailPlatform: undefined,
        emailMarketingTool: undefined,
        email: undefined,
        companyName: undefined,
        step2Data: undefined,
      }),
    }),
    {
      name: 'registration-storage',
      // Only persist non-sensitive data (step2Data excluded — contains password)
      partialize: (state) => ({
        step: state.step,
        emailPlatform: state.emailPlatform,
        emailMarketingTool: state.emailMarketingTool,
        email: state.email,
        companyName: state.companyName,
      }),
    }
  )
);

export { useSetupStore } from './setupStore';
export type { SetupState, EnrichmentFeature, NameDetail } from './setupStore';
